import * as React from "react";
import {
    Alert,
    Button,
    Header,
    SpaceBetween,
    Spinner,
    Tabs
} from "@amzn/awsui-components-react";
import { PageOverviewTab } from "../../components/page/PageOverview";
import { AppContext } from "../../setup/context";
import { useAPI } from "../../hooks/api-hook";
import { UIModel } from "../../model/ui-model";
import { EntityFactory, IPage, Page, PageLibraryFactory } from "@amzn/ask-legal-domain";
import { PageDraftDetail } from "../../components/page-draft/PageDraftDetail";
import { EntityPermissionDetail } from "../../components/common/EntityPermissionComp";
import { PageVersionHistory } from "../../components/page/PageVersionHistoryTab";
import { PagePreferencesTab } from "../../components/page/PagePreferencesTab";
import { IdentityLink } from "../../components/common/IdentityLink";
import { PermissionDenied } from "../redirects/PermissionDenied";
import { PageNotFound } from "../redirects/PageNotFound";
import { useNavigate } from "react-router";
import { getQueryParameterMap } from "../../utils/common-utils";
import { useLocation, useNavigationType } from "react-router-dom";
import { PermissionsBaselineSection } from "../../components/common/PermissionsBaselineSection";
import { AfrEnabledSection } from "../../components/common/AfrEnabledSection";

export const PagePortal = (props: {
    id: string;
    activeTabId?: string;
}) => {
    const context = React.useContext(AppContext);
    const navigate = useNavigate();
    const loadPageRunner = useAPI(
        context.getPageAPI().checkAdminAccess
    );
    const pageDataState = UIModel.State.use<Page.Data>({});
    const DEFAULT_TAB_ID = "overview";
    const [activeTabId, setActiveTabId] = React.useState(props.activeTabId ? props.activeTabId : DEFAULT_TAB_ID);
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [copyButtonText, setCopyButtonText] = React.useState<string>("Link to Live Page");
    const location = useLocation();
    const navigationType = useNavigationType();
    const baselinePagePermissionsRunner = useAPI(context.getPageAPI().updateMetadata);
    const loadPermissionRunner = useAPI(context.getEntityPermissionAPI().load);
    const [isPublic, setIsPublic] = React.useState<boolean | null>(null);

    const protectedEditorUserIds = React.useMemo(() => {
        const ids = pageDataState.value ? [
            pageDataState.value.pageOwner?.id,
            ...(pageDataState.value.secondaryPageOwners || []).map(o => o.id)
        ].filter(Boolean) as string[] : [];
        return ids.length ? ids : undefined;
    }, [pageDataState.value]);

    const protectedEditorTeamIds = React.useMemo(
        () => pageDataState.value?.secondaryPageOwnerTeams?.length
            ? pageDataState.value.secondaryPageOwnerTeams.map(t => t.teamId)
            : undefined,
        [pageDataState.value]
    );

    const setTabURL = (tabId: string): string => {
        const currenthash = window.location.hash.slice(1).split("?")[0];
        // Clear any other params currently since they have no meaning across tabs
        return `${currenthash}?tab=${tabId}`;
    };

    const baselinePagePermissions = () => {
        baselinePagePermissionsRunner.submitRun(
            IPage.UpdatePageMetadataInput.create({
                baselinePagePermissions: true,
                targetRef: EntityFactory.toEntityRef(pageDataState.value)
            })
        );
    };

    React.useEffect(() => {
        // Hook to set default entry tabId url
        const queryParamMap = getQueryParameterMap();
        if (!queryParamMap.get("tab")) {
            window.history.replaceState(
                null,
                "",
                `${window.location.hash}?tab=${DEFAULT_TAB_ID}`
            );
        }
    }, []);

    React.useEffect(() => {
        loadPageRunner.submitRun(
            props.id
        );
    }, [props.id]);

    React.useEffect(() => {
        if (navigationType === "POP") {
            const urlTab = location.search.slice(1).split("&").map(v => v.split("=")).filter(x => x[0] === "tab")[0];
            if (!urlTab) {
                setActiveTabId(DEFAULT_TAB_ID);
                return;
            }
            setActiveTabId(urlTab[1]);
        }
    }, [location, navigationType]);

    React.useEffect(() => {
        if (loadPageRunner.status === "Succeeded") {
            pageDataState.setValue(loadPageRunner.data.output);
            // Load the parent page library's permission to check if it's public
            const plId = PageLibraryFactory.fromEntityId(props.id);
            loadPermissionRunner.submitRun(plId);
        }
    }, [loadPageRunner.status]);

    React.useEffect(() => {
        if (loadPermissionRunner.status === "Succeeded") {
            setIsPublic(!!loadPermissionRunner.data.output.isPublicViewOnly);
        }
    }, [loadPermissionRunner.status]);

    const getLivePageUrl = (): string => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/#/page/${props.id}/live`;
    };

    const handleCopyLivePageLink = async () => {
        const livePageUrl = getLivePageUrl();
        try {
            await navigator.clipboard.writeText(livePageUrl);
            setCopyButtonText("Link Copied!");
            setTimeout(() => {
                setCopyButtonText("Link to Live Page");
            }, 1000);
        } catch (err) {
            console.error("Failed to copy link:", err);
        }
    };

    return (
        <React.Fragment>
            <SpaceBetween size="s" direction="vertical">
                {loadPageRunner.status === "Succeeded" && !!pageDataState.value && pageDataState.value.deprecated &&
                    <Alert
                        type="info"
                        header="Deprecated">
                        This page was last updated on {
                            pageDataState.value.lastModifiedDate
                        } by&nbsp;
                        <IdentityLink
                            id={pageDataState.value.lastModifiedBy.id}
                            name={pageDataState.value.lastModifiedBy.name}
                        /> with message "{pageDataState.value.lastModifiedCommit}"
                    </Alert>
                }
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Header id="pageHeader" variant="h1">Page: {props.id.split("-")[2]}</Header>
                    {loadPageRunner.status === "Succeeded" && (
                        <Button
                            iconName="copy"
                            onClick={handleCopyLivePageLink}
                            variant="primary"
                            ariaLabel="Click to copy and share the link with users"
                            wrapText={false}
                        >
                            {copyButtonText}
                        </Button>
                    )}
                </div>
                <React.Fragment>
                    {loadPageRunner.status === "Running" && <Spinner />}
                    {loadPageRunner.status === "Error" && (
                        loadPageRunner.data.err.code === 403 ?
                            <PermissionDenied entityId={props.id} accessType="PageAdminPortal"/> : <PageNotFound/>
                    )}
                    {loadPageRunner.status === "Succeeded" && !pageDataState.value && <Spinner />}
                    {loadPageRunner.status === "Succeeded" && !!pageDataState.value &&
                        <Tabs
                            tabs={[
                                {
                                    label: "Overview",
                                    id: "overview",
                                    content:  <PageOverviewTab
                                        state={pageDataState}
                                        reload={loadPageRunner.reload}
                                        setActiveTab={setActiveTabId}
                                    />
                                },
                                {
                                    label: "Permissions",
                                    id: "permissions",
                                    content: <SpaceBetween size="l">
                                        <PermissionsBaselineSection
                                            permissionsBaselinedDate={pageDataState.value.permissionsBaselinedDate}
                                            runner={baselinePagePermissionsRunner}
                                            onBaseline={baselinePagePermissions}
                                            onBaselineSuccess={(output) => pageDataState.setValue(output)}
                                        />
                                        <EntityPermissionDetail.PreloadComp
                                            id={props.id}
                                            protectedEditorUserIds={protectedEditorUserIds}
                                            protectedEditorTeamIds={protectedEditorTeamIds}
                                        />
                                        {isPublic === false && <AfrEnabledSection
                                            page={pageDataState.value}
                                            onUpdate={(updated) => pageDataState.setValue(updated)}
                                        />}
                                    </SpaceBetween>
                                },
                                {
                                    label: "Draft",
                                    id: "draftPage",
                                    content: <PageDraftDetail.Preload
                                        pageRef={EntityFactory.toEntityRef(pageDataState.value)}
                                        pageData={pageDataState.value}
                                        setIsEditing={setIsEditing}
                                    />
                                },
                                {
                                    label: "Version History",
                                    id: "versionHistory",
                                    content: <PageVersionHistory.TableView state={pageDataState}/>
                                },
                                {
                                    label: "Preferences",
                                    id: "preferences",
                                    content: <PagePreferencesTab
                                        pageId={props.id}
                                        containerIds={pageDataState.value.sections.flatMap(
                                            section => section.containers.map(container => container.id)
                                        )}
                                        containerMetadata={pageDataState.value.sections.flatMap(
                                            section => section.containers
                                        ).reduce((map, container) => {
                                            map[container.id] = container;
                                            return map;
                                        }, {})}
                                    />
                                }
                            ]}
                            activeTabId={activeTabId}
                            onChange={(e) => {
                                if (isEditing) {
                                    window.alert("You have unsaved changes. Please save them to continue");
                                    return;
                                }
                                setActiveTabId(e.detail.activeTabId);
                                navigate(setTabURL(e.detail.activeTabId));
                            }}
                        />
                    }
                </React.Fragment>
            </SpaceBetween>
        </React.Fragment>
    );
};
